import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import './AdminReviews.css';

const AdminReviews = () => {
  const [tab, setTab] = useState('product');
  const [productReviews, setProductReviews] = useState([]);
  const [homeReviews, setHomeReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState('');

  // New home review form
  const [hrForm, setHrForm] = useState({ customerName: '', comment: '', rating: 5, photo: '' });
  const [hrLoading, setHrLoading] = useState(false);

  useEffect(() => {
    api.get('/products?limit=100').then(({ data }) => setProducts(data.products));
    api.get('/reviews/home/featured').then(({ data }) => setHomeReviews(data));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedProduct) {
      setLoading(true);
      api.get(`/reviews/${selectedProduct}`).then(({ data }) => setProductReviews(data)).finally(() => setLoading(false));
    }
  }, [selectedProduct]);

  const deleteProductReview = async (id) => {
    if (!window.confirm('Delete this review?')) return;
    try {
      await api.delete(`/reviews/${id}`);
      setProductReviews(prev => prev.filter(r => r._id !== id));
      toast.success('Review deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const deleteHomeReview = async (id) => {
    if (!window.confirm('Delete this home review?')) return;
    try {
      await api.delete(`/reviews/home/featured/${id}`);
      setHomeReviews(prev => prev.filter(r => r._id !== id));
      toast.success('Review deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const addHomeReview = async (e) => {
    e.preventDefault();
    setHrLoading(true);
    try {
      const { data } = await api.post('/reviews/home/featured', hrForm);
      setHomeReviews(prev => [...prev, data]);
      setHrForm({ customerName: '', comment: '', rating: 5, photo: '' });
      toast.success('Home review added!');
    } catch {
      toast.error('Failed to add review');
    } finally {
      setHrLoading(false);
    }
  };

  return (
    <div>
      <div className="admin-page-title">Reviews</div>
      <p className="admin-page-sub">Manage product reviews and homepage customer reviews</p>

      <div className="reviews-tabs">
        <button className={`rev-tab ${tab === 'product' ? 'active' : ''}`} onClick={() => setTab('product')}>
          📝 Product Reviews
        </button>
        <button className={`rev-tab ${tab === 'home' ? 'active' : ''}`} onClick={() => setTab('home')}>
          🏠 Homepage Reviews ({homeReviews.length})
        </button>
      </div>

      {tab === 'product' && (
        <div className="product-reviews-tab">
          <div className="admin-filters" style={{ marginBottom: 20 }}>
            <div className="filter-group">
              <span className="filter-label">Select Product:</span>
              <select className="filter-select" value={selectedProduct} onChange={e => setSelectedProduct(e.target.value)} style={{ minWidth: 260 }}>
                <option value="">Choose a product...</option>
                {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </select>
            </div>
          </div>

          {selectedProduct && (
            <div className="admin-table-wrap">
              <div className="table-header">
                <span className="table-title">{productReviews.length} Reviews</span>
              </div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
              ) : productReviews.length === 0 ? (
                <div className="table-empty">No reviews for this product yet.</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Rating</th>
                      <th>Comment</th>
                      <th>Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productReviews.map(rev => (
                      <tr key={rev._id}>
                        <td className="rev-user-cell">
                          <div className="rev-mini-avatar">{rev.name?.charAt(0).toUpperCase()}</div>
                          <span>{rev.name}</span>
                        </td>
                        <td>
                          <span className="rev-stars-mini">{'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}</span>
                          <span className="rev-rating-val"> {rev.rating}/5</span>
                        </td>
                        <td className="rev-comment-cell">{rev.comment}</td>
                        <td className="date-cell">{new Date(rev.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button className="action-btn delete" onClick={() => deleteProductReview(rev._id)}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {!selectedProduct && (
            <div className="select-product-prompt">
              <span>👆</span>
              <p>Select a product above to view its reviews</p>
            </div>
          )}
        </div>
      )}

      {tab === 'home' && (
        <div className="home-reviews-tab">
          {/* Add Form */}
          <div className="add-home-review card">
            <h3>➕ Add Homepage Review</h3>
            <form onSubmit={addHomeReview}>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Customer Name *</label>
                  <input className="form-input" value={hrForm.customerName} onChange={e => setHrForm(p => ({...p, customerName: e.target.value}))} required placeholder="e.g. Rahim Ahmed" />
                </div>
                <div className="form-group">
                  <label className="form-label">Rating (1-5)</label>
                  <select className="form-input" value={hrForm.rating} onChange={e => setHrForm(p => ({...p, rating: Number(e.target.value)}))}>
                    {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} ⭐</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Comment *</label>
                <textarea className="form-input" rows={3} value={hrForm.comment} onChange={e => setHrForm(p => ({...p, comment: e.target.value}))} required placeholder="Customer's review text..." style={{ resize: 'vertical' }} />
              </div>
              <div className="form-group">
                <label className="form-label">Customer Photo URL (optional)</label>
                <input className="form-input" value={hrForm.photo} onChange={e => setHrForm(p => ({...p, photo: e.target.value}))} placeholder="https://..." />
              </div>
              <button type="submit" className="btn btn-primary" disabled={hrLoading}>
                {hrLoading ? 'Adding...' : '➕ Add Review'}
              </button>
            </form>
          </div>

          {/* Home Reviews List */}
          <div className="home-reviews-grid">
            {homeReviews.map(rev => (
              <div key={rev._id} className="home-rev-card card">
                <div className="hr-header">
                  <div className="hr-customer">
                    {rev.photo ? (
                      <img src={rev.photo} alt={rev.customerName} className="hr-avatar" />
                    ) : (
                      <div className="hr-avatar-placeholder">{rev.customerName.charAt(0)}</div>
                    )}
                    <div>
                      <strong>{rev.customerName}</strong>
                      <span>{'★'.repeat(rev.rating || 5)}</span>
                    </div>
                  </div>
                  <button className="action-btn delete" onClick={() => deleteHomeReview(rev._id)}>🗑</button>
                </div>
                <p className="hr-comment">"{rev.comment}"</p>
                <span className="hr-date">{new Date(rev.createdAt).toLocaleDateString()}</span>
              </div>
            ))}
            {homeReviews.length === 0 && (
              <div className="no-reviews-msg">No homepage reviews yet. Add one above.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
